import React, { useEffect, useMemo } from 'react';
import { Box, Button, makeStyles } from 'housecall-ui';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';

import { useToastController } from 'components/Common/Toast';
import { useOrgData } from 'components/OnlineBooking/hooks/useOrgData';

import { CompanyLogo } from './components/CompanyLogo';
import { DataError } from './components/DataError';
import { ListSkeleton } from './components/ListSkeleton';
import { Spinner } from './components/Spinner';
import { StepProgressBar } from './components/StepProgressBar';
import { useBookingConfirmationMutation } from './hooks/useBookingConfirmationMutation';
import { useEventTracking } from './hooks/useEventTracking';
import { useServices } from './hooks/useServices';
import { useOnlineBookingReducer } from './state/useOnlineBookingReducer';
import { HOMEOWNER_FLOW_STEP_MAP } from './Steps/consts';

const useStyles = makeStyles((theme: HCPTheme) => ({
  actionButtons: {
    backgroundColor: theme.palette.background.main,
    display: 'flex',
    gap: `${theme.spacing(2)}px`,
    position: 'absolute',
    bottom: '0',
    left: '0',
    width: '100%',
    zIndex: theme.zIndex.snackbar,
    padding: theme.spacing(2),
    boxShadow: theme.shadows[4],
  },
  stepWrapper: {
    minHeight: '0',
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
  },
}));

const HOMEOWNER_FLOW_STEP_COUNT = 5;

export const HomeOwnerFlowContent = () => {
  const { actionButtons, stepWrapper } = useStyles();
  const { state, dispatch, actions } = useOnlineBookingReducer();
  const { step, orgUuid, contactDetails, service, description, arrivalWindow, question1, question2 } = state;
  const { isError: isOrgError, data: orgData } = useOrgData({ orgUuid });

  const { isLoading, isFetching, isError } = useServices({ orgUuid });
  const { mutate, isLoading: mutationLoading } = useBookingConfirmationMutation();
  const { showToast } = useToastController();

  const selectedStep = HOMEOWNER_FLOW_STEP_MAP[step];

  if (!selectedStep) {
    throw new Error(`Selected step ${step} is out of scope!`);
  }
  let ContentComponent;

  const { component: StepComponent, displayedStep, isNextButtonDisabled, name: stepName } = selectedStep;

  ContentComponent = StepComponent;

  if (isLoading || isFetching) {
    ContentComponent = ListSkeleton;
  }

  if (isError || isOrgError) {
    ContentComponent = DataError;
  }

  const trackingProps = useMemo(
    () => ({
      pro_impersonated: false,
      email: orgData?.email || '',
      pro_uuid: orgData?.founding_pro_uuid || '',
    }),
    [orgData]
  );

  const { trackPage, trackEvent } = useEventTracking(trackingProps);

  const handleBackClick = () => {
    dispatch(actions.previousStep());

    if (stepName === 'description') {
      dispatch(actions.updateDescription(''));
    }
  };

  const handleNextClick = () => {
    if (stepName === 'contact-details') {
      return dispatch(actions.startSubmittingContactDetailsForm());
    }

    if (stepName === 'confirmation') {
      mutate(
        {
          service,
          orgUuid,
          description,
          contactDetails,
          arrivalWindow,
          question1: question1 ? `\n${service?.question1} \n- ${question1}` : '',
          question2: question2 ? `\n${service?.question2} \n- ${question2}` : '',
        },
        {
          onSuccess: () => {
            dispatch(actions.setSuccess());
            trackEvent('Online booking - successfully booked');
          },
          onError: () => {
            showToast('Failed to confirm booking, please try again.');
          },
        }
      );
    }
    dispatch(actions.nextStep());
  };

  useEffect(() => {
    if (orgData && selectedStep && selectedStep.name !== 'categories') {
      trackPage('Online Booking - Homeowner flow', {
        step: selectedStep.displayedStep,
        step_name: selectedStep.name,
      });
    }
  }, [selectedStep, trackPage, orgData]);

  const isDisabled = typeof isNextButtonDisabled === 'boolean' ? isNextButtonDisabled : isNextButtonDisabled(state);

  return (
    <>
      <CompanyLogo />
      <StepProgressBar currentStep={displayedStep} stepCount={HOMEOWNER_FLOW_STEP_COUNT} />
      <div className={stepWrapper}>{ContentComponent && <ContentComponent />}</div>
      <Box className={actionButtons}>
        {step > 1 && (
          <Button onClick={handleBackClick} size="large" fullWidth variant="outlined" color="primary">
            Back
          </Button>
        )}
        <Button
          onClick={handleNextClick}
          size="large"
          fullWidth
          variant="contained"
          color="primary"
          disabled={isDisabled}
        >
          {stepName === 'confirmation' ? 'Confirm Booking' : 'Next'}
        </Button>
      </Box>

      <Spinner show={mutationLoading} />
    </>
  );
};
